/**
 * Client approval trail — the one component rendered on the client portal, embedded
 * read-only in staff Client Portal management, and shown in the PM "Client view"
 * pop-out from Project Hub › Files. It is fed by `buildApprovalTrail`, so every
 * surface sees the same branching timeline.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, FileText, GitFork } from 'lucide-react';
import {
  buildApprovalTrail,
  trailStationId,
  TRAIL_MEDIA_LABELS,
  type BuildApprovalTrailInput,
  type TrailItem,
  type TrailLane,
  type TrailStation,
} from '../../utils/portalApprovalTrail';
import { PORTAL_DESIGN_PHASE_LABELS, portalReleaseDesignPhase } from '../../utils/portalReleasePhase';
import type { PortalDesignPhase } from '../../types';
import { structureSelectionLabel, structureSelectionTitle } from '../../utils/structureSelectionLabel';
import './PortalApprovalTrail.css';

export interface PortalApprovalTrailProps extends BuildApprovalTrailInput {
  /** Hide approve actions (stakeholder / sample portals, staff previews). */
  readOnly?: boolean;
  /** Tighter paddings for the pop-out panel. */
  compact?: boolean;
  /** Open the released file for an item (client copy). */
  onOpenItem?: (item: TrailItem) => void;
  /** Start the signature flow for an awaiting item. */
  onApprove?: (item: TrailItem) => void;
  /** Optional heading override. */
  title?: string;
  /** Render nothing when there are no releases (default true — legacy portals unchanged). */
  hideWhenEmpty?: boolean;
}

function fmtDate(d?: Date): string {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function mediaIcon(media: TrailItem['media']) {
  return media === '3d' ? <Box size={14} aria-hidden /> : <FileText size={14} aria-hidden />;
}

/** Phase belongs to the release, not the column it was opened from. */
function phaseLabelForItem(item: TrailItem): string {
  const target = item.release.portalTarget;
  if (target === 'engineered_drawings') return PORTAL_DESIGN_PHASE_LABELS.engineering;
  if (target === 'concept_elevations') return PORTAL_DESIGN_PHASE_LABELS.concept;
  return PORTAL_DESIGN_PHASE_LABELS[portalReleaseDesignPhase(item.release)];
}

function historyStatusLabel(status: TrailItem['status'], canApprove: boolean): string {
  switch (status) {
    case 'approved':
      return 'Approved';
    case 'awaiting':
      return canApprove ? 'Review & approve' : 'Awaiting approval';
    case 'superseded':
      return 'Replaced';
    default:
      return 'Released';
  }
}

function stationTitle(st: TrailStation, laneLabel: string): string {
  const base = `${laneLabel} · ${st.label}`;
  if (!st.item) return `${base} — not released yet`;
  const v = `v${st.item.releaseVersionIndex}`;
  switch (st.status) {
    case 'approved':
      return `${base} ${v} — approved by ${st.item.approval?.approvedByName || 'client'}${
        st.item.approval?.approvedAt ? ` · ${fmtDate(st.item.approval.approvedAt)}` : ''
      }`;
    case 'shared':
      return `${base} ${v} — approved for the whole project before the split`;
    case 'awaiting': {
      const prior = st.priorApproval;
      const priorNote = prior
        ? ` Previously approved${
            prior.scope.structureIds.length === 0 ? ' for the whole project' : ` for ${prior.scope.label}`
          }${prior.approval?.approvedAt ? ` · ${fmtDate(prior.approval.approvedAt)}` : ''}.`
        : '';
      const fileCount = st.item?.releases.length ?? 0;
      const filesNote = fileCount > 1 ? ` ${fileCount} files.` : '';
      return `${base} ${v} — awaiting your approval.${filesNote}${priorNote}`;
    }
    case 'informational':
      return `${base} ${v} — released for review`;
    default:
      return base;
  }
}

const PortalApprovalTrail: React.FC<PortalApprovalTrailProps> = ({
  readOnly = false,
  compact = false,
  onOpenItem,
  onApprove,
  title,
  hideWhenEmpty = true,
  ...input
}) => {
  const trail = useMemo(
    () => buildApprovalTrail(input),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [input.releases, input.bundles, input.legacyApprovals, input.structures, input.structureGroups, input.files, input.streams, input.proposalApprovedAt, input.proposalApprovedByName],
  );
  const [openHistoryKey, setOpenHistoryKey] = useState<string | null>(null);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!openHistoryKey) return undefined;
    const onPointer = (event: MouseEvent) => {
      if (!sectionRef.current?.contains(event.target as Node)) setOpenHistoryKey(null);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenHistoryKey(null);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [openHistoryKey]);

  if (trail.isEmpty && hideWhenEmpty) return null;

  // Structure rows once the project has split. The trunk stays in the model so
  // whole-project releases still fill those stops on each structure.
  const lanesAll: TrailLane[] = trail.lanes.length > 0 ? trail.lanes : trail.trunk ? [trail.trunk] : [];
  const cols = trail.stations.length;
  const colIndex = new Map<string, number>();
  trail.stations.forEach((k, i) => colIndex.set(trailStationId(k), i));
  // Contiguous runs of columns per phase (stations are already in canonical order).
  const phaseBands: { phase: PortalDesignPhase; start: number; span: number }[] = [];
  trail.stations.forEach((k, i) => {
    const last = phaseBands[phaseBands.length - 1];
    if (last && last.phase === k.phase) last.span += 1;
    else phaseBands.push({ phase: k.phase, start: i, span: 1 });
  });
  const phaseStartCols = new Set(phaseBands.map(b => b.start));
  // One header row (Structures + 2D/3D). Lanes start on the next row.
  const LANE_ROW_OFFSET = 2;
  const singlePhase = phaseBands.length <= 1;
  const depthOf = (lane: TrailLane): number => {
    let d = lane.isTrunk ? 0 : 1;
    let p = lane.parentKey;
    while (p) {
      d += 1;
      const parent = trail.lanes.find(l => l.key === p);
      p = parent ? parent.parentKey : null;
    }
    return Math.min(d, 2);
  };

  const canApprove = !readOnly && !!onApprove;

  const historyFor = (lane: TrailLane, st: TrailStation): TrailItem[] => {
    const keys = new Set<string>([lane.key]);
    let parentKey = lane.parentKey;
    while (parentKey) {
      keys.add(parentKey);
      const parent = trail.lanes.find(l => l.key === parentKey);
      parentKey = parent ? parent.parentKey : null;
    }
    if (trail.trunk) keys.add(trail.trunk.key);
    const addItemScopes = (item: TrailItem | null) => {
      if (!item) return;
      keys.add(item.scope.key);
      item.scopes.forEach(scope => keys.add(scope.key));
    };
    addItemScopes(st.item);
    addItemScopes(st.priorApproval);
    return trail.items
      .filter(item => item.phase === st.phase && item.media === st.media && item.scopes.some(scope => keys.has(scope.key)))
      .sort((a, b) => b.releasedAt.getTime() - a.releasedAt.getTime());
  };

  const openItem = (item: TrailItem) => {
    setOpenHistoryKey(null);
    onOpenItem?.(item);
  };

  const approveItem = (item: TrailItem) => {
    setOpenHistoryKey(null);
    onApprove?.(item);
  };

  const renderLane = (lane: TrailLane, idx: number) => {
    const depth = depthOf(lane);
    const siblings = lanesAll.filter(l => !l.isTrunk && l.parentKey === lane.parentKey);
    const isLastChild = !lane.isTrunk && siblings[siblings.length - 1]?.key === lane.key;
    return (
      <li
        key={lane.key}
        className={`cps-trail-lane${lane.isTrunk ? ' cps-trail-lane--trunk' : ' cps-trail-lane--child'}${
          depth >= 2 ? ' cps-trail-lane--depth-2' : ''
        }${isLastChild ? ' cps-trail-lane--last-child' : ''}`}
        aria-label={`${lane.label}: ${lane.summary}`}
      >
        <div className="cps-trail-lane-label" style={{ gridColumn: 1, gridRow: idx + LANE_ROW_OFFSET }}>
          <span className="cps-trail-lane-name" title={structureSelectionTitle(lane.structureNames) || lane.label}>
            {lane.label}
          </span>
          {lane.structureNames.length > 1 &&
          lane.label !== structureSelectionLabel(lane.structureNames, { maxNamed: lane.structureNames.length }) ? (
            <span className="cps-trail-lane-members">{lane.structureNames.join(' · ')}</span>
          ) : null}
          {!lane.isTrunk &&
          lane.stations.some(s => s.status === 'shared') &&
          lane.stations.every(s => !s.item || s.inherited) ? (
            <span className="cps-trail-lane-note">Approved with the whole project</span>
          ) : null}
        </div>
        <ol className="cps-trail-stations" aria-label={`${lane.label} stops`}>
          {lane.stations.map((st) => {
            const col = colIndex.get(st.id) ?? 0;
            const history = st.item ? historyFor(lane, st) : [];
            const historyKey = `${lane.key}|${st.id}`;
            const historyOpen = openHistoryKey === historyKey;
            const stopClass = `cps-trail-stop cps-trail-stop--${st.status === 'shared' ? 'approved' : st.status}`;
            const label = stationTitle(st, lane.label);
            const fileCount = st.status === 'awaiting' ? (st.item?.releases.length ?? 0) : 0;
            const fileBadge = fileCount > 1 ? (
              <span className="cps-trail-stop-count" aria-hidden>{fileCount}</span>
            ) : null;
            return (
              <li
                key={st.id}
                className={`cps-trail-station cps-trail-station--${st.status}${
                  phaseStartCols.has(col) && col > 0 ? ' cps-trail-station--phase-start' : ''
                }`}
                style={{ gridColumn: col + 2, gridRow: idx + LANE_ROW_OFFSET }}
              >
                {history.length > 0 ? (
                  <button
                    type="button"
                    className={stopClass}
                    title={label}
                    aria-label={label}
                    aria-expanded={historyOpen}
                    onClick={() => setOpenHistoryKey(historyOpen ? null : historyKey)}
                  >
                    {mediaIcon(st.media)}
                    {fileBadge}
                  </button>
                ) : (
                  <span className={stopClass} title={label} aria-label={label} role="img">
                    {fileBadge}
                  </span>
                )}
                {st.item && !st.inherited ? (
                  <span className="cps-trail-stop-version" aria-hidden>
                    v{st.item.releaseVersionIndex}
                  </span>
                ) : null}
                {st.status === 'awaiting' && st.priorApproval ? (
                  <span className="cps-trail-prior-approval">
                    {st.priorApproval.scope.structureIds.length === 0
                      ? 'Whole project approved'
                      : `Approved for ${st.priorApproval.scope.label}`}
                  </span>
                ) : null}
                {historyOpen ? (
                  <div className="cps-trail-history" role="menu">
                    {history.map(item => {
                      const rowLabel = `${item.displayLabel} v${item.releaseVersionIndex}`;
                      const canView = !!onOpenItem;
                      const canSign = item.status === 'awaiting' && canApprove;
                      return (
                        <div
                          key={item.id}
                          className={`cps-trail-history-row cps-trail-history-row--${item.status}`}
                        >
                          <span className={`cps-trail-stop cps-trail-stop--mini cps-trail-stop--${item.status}`} aria-hidden>
                            {mediaIcon(item.media)}
                          </span>
                          <span className="cps-trail-history-copy">
                            <span className="cps-trail-history-name">{rowLabel}</span>
                            <span className="cps-trail-history-meta">
                              {phaseLabelForItem(item)}
                              {' · '}
                              {historyStatusLabel(item.status, false)}
                              {item.releasedAt ? ` · ${fmtDate(item.releasedAt)}` : ''}
                            </span>
                          </span>
                          {canView ? (
                            <button type="button" className="cps-trail-history-action" onClick={() => openItem(item)}>
                              View
                            </button>
                          ) : null}
                          {canSign ? (
                            <button type="button" className="cps-trail-history-action cps-trail-history-action--approve" onClick={() => approveItem(item)}>
                              Approve
                            </button>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ol>
      </li>
    );
  };

  const awaitingApproval = trail.counts.awaiting;

  return (
    <section
      ref={sectionRef}
      className={`cps-trail${compact ? ' cps-trail--compact' : ''}`}
      aria-label="Design approval trail"
      data-tour="portal-approval-trail"
    >
      <header className="cps-trail-head">
        <h3 className="cps-trail-title">{title ?? 'Design approvals'}</h3>
        <div className="cps-trail-head-side">
          {awaitingApproval > 0 ? (
            <p className="cps-trail-score" role="status">
              {awaitingApproval} need{awaitingApproval === 1 ? 's' : ''} approval
            </p>
          ) : null}
          <div className="cps-trail-legend">
            <span className="cps-trail-legend-item"><span className="cps-trail-legend-mark cps-trail-legend-mark--approved" /> Approved</span>
            <span className="cps-trail-legend-item"><span className="cps-trail-legend-mark cps-trail-legend-mark--awaiting" /> Needs approval</span>
            <span className="cps-trail-legend-item"><span className="cps-trail-legend-mark cps-trail-legend-mark--superseded" /> Superseded</span>
          </div>
        </div>
      </header>

      {trail.lanes.length > 0 && trail.forkAt ? (
        <p className="cps-trail-fork-note">
          <GitFork size={12} aria-hidden />
          <span className="cps-trail-fork-tip" tabIndex={0}>
            Split into groups {fmtDate(trail.forkAt)}
            <span className="cps-trail-fork-tip-body" role="tooltip">
              The whole project was broken into separate groups. Each group has its own timeline and its own approvals, and now moves on its own track.
            </span>
          </span>
        </p>
      ) : null}

      {cols > 0 ? (
        <ol
          className={`cps-trail-map${compact ? ' cps-trail-map--compact' : ''}`}
          style={{ ['--trail-cols' as string]: cols } as React.CSSProperties}
          aria-label="Approval timeline"
        >
          <li className="cps-trail-colhead">
            <span className="cps-trail-colhead-structures" style={{ gridColumn: 1, gridRow: 1 }}>
              Structure groups
            </span>
            {trail.stations.map((k, i) => (
              <span key={trailStationId(k)} className="cps-trail-colhead-cell" style={{ gridColumn: i + 2, gridRow: 1 }}>
                {singlePhase ? TRAIL_MEDIA_LABELS[k.media] : `${PORTAL_DESIGN_PHASE_LABELS[k.phase]} ${TRAIL_MEDIA_LABELS[k.media]}`}
              </span>
            ))}
          </li>
          {lanesAll.map((lane, idx) => renderLane(lane, idx))}
        </ol>
      ) : null}
    </section>
  );
};

/** Error boundary so a data oddity on an old portal can never blank the page. */
export class PortalApprovalTrailBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  componentDidCatch(error: unknown): void {
    // eslint-disable-next-line no-console
    console.error('[PortalApprovalTrail] render failed', error);
  }

  render(): React.ReactNode {
    if (this.state.failed) return this.props.fallback ?? null;
    return this.props.children;
  }
}

export default PortalApprovalTrail;
